import React, { useState, useEffect } from 'react'
import { Link, useLoaderData, useLocation, useNavigate, useNavigation } from '@remix-run/react'
import { defer } from '@remix-run/node'
import Header from '../components/header'
import Footer from '../components/footer'
import Loading from '../components/loading'
import SharePopover from '../components/sharePopover'
import { MagnifyingGlassIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import { fetchWpCategories, fetchWpAuthors } from '../utils/api.server'

// Constants for excluded categories
const EXCLUDED_SLUGS = ['uncategorized', 'uncategorized-fr', 'uncategorized-es', 'uncategorized-ar', 'archived']

export function meta() {
  return [
    { title: 'News - TalentLift Canada' },
    {
      name: 'description',
      content:
        'There is immense talent within refugee populations. We are a non-profit talent sourcing partner helping employers source and relocate talent from within refugee populations.',
    },
    {
      property: 'og:title',
      content: 'News - TalentLift Canada',
    },
    {
      property: 'og:description',
      content:
        'There is immense talent within refugee populations. We are a non-profit talent sourcing partner helping employers source and relocate talent from within refugee populations.',
    },
    {
      property: 'og:image',
      content: '/images/banners/news_preview.png',
    },
    {
      property: 'og:url',
      content: 'https://www.talentlift.ca/news',
    },
  ]
}

// Loader function
export const loader = async () => {
  const wpBackend = process.env.WORDPRESS_BACKEND
  const categories = await fetchWpCategories()
  const authors = await fetchWpAuthors()

  const filteredCategories = [
    { name: 'All', id: '' },
    ...categories.filter((cat) => !EXCLUDED_SLUGS.includes(cat.slug)),
  ]

  return defer({
    wpBackend,
    wpCategories: filteredCategories,
    baseUrl: process.env.BASE_URL,
    authors,
  })
}

// Helper function to fetch posts
const fetchPosts = async (wpBackend, categoryId = '', searchQuery = '', authorId = '', page = 1, perPage = 10) => {
  const categoryFilter = categoryId ? `&categories=${categoryId}` : ''
  const searchFilter = searchQuery ? `&search=${searchQuery}` : ''
  const authorFilter = authorId ? `&author=${authorId}` : ''

  try {
    const response = await fetch(
      `${wpBackend}/posts?page=${page}&per_page=${perPage}${categoryFilter}${searchFilter}${authorFilter}`
    )
    const totalPosts = response.headers.get('X-WP-Total')
    const totalPages = response.headers.get('X-WP-TotalPages')
    const data = await response.json()
    return {
      posts: Array.isArray(data) ? data : [],
      totalPosts: parseInt(totalPosts, 10) || 0,
      totalPages: parseInt(totalPages, 10) || 1,
    }
  } catch (error) {
    console.error('Error fetching posts:', error)
    return { posts: [], totalPosts: 0, totalPages: 1 }
  }
}

// Helper function to get category name
const getCategoryName = (categoryId, categories) => {
  const category = categories.find((cat) => cat.id === categoryId)
  return category ? category.name : 'Uncategorized'
}

export default function NewsIndex() {
  const { wpBackend, wpCategories, baseUrl, authors } = useLoaderData()
  const [posts, setPosts] = useState([])
  const [activeCategory, setActiveCategory] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [author, setAuthor] = useState('')
  const [authorName, setAuthorName] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalPosts, setTotalPosts] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isClient, setIsClient] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const navigation = useNavigation()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const categorySlug = searchParams.get('c')
    const authorSlug = searchParams.get('a')
    const searchQuery = searchParams.get('s')

    let categoryId = ''
    let authorId = ''

    if (categorySlug) {
      const categoryData = wpCategories.find((cat) => cat.slug === categorySlug)
      if (categoryData) {
        categoryId = categoryData.id
      }
    }

    if (authorSlug) {
      const authorData = authors.find((author) => author.slug.toLowerCase() === authorSlug.toLowerCase())
      if (authorData) {
        authorId = authorData.id
        setAuthorName(authorData.name) // Set author name for display
      }
    }

    // Update filters
    setActiveCategory(categoryId || '')
    setSearchQuery(searchQuery || '')
    setAuthor(authorId || '')

    // Fetch posts for the updated filters and current page
    setLoading(true)
    fetchPosts(wpBackend, categoryId, searchQuery, authorId, currentPage).then(({ posts, totalPosts, totalPages }) => {
      setPosts(posts)
      setTotalPosts(totalPosts)
      setTotalPages(totalPages)
      setLoading(false)
    })

    setIsClient(true)
  }, [location.search, currentPage, wpCategories, authors, wpBackend])

  const handleCategoryClick = (categoryId, categorySlug) => {
    setCurrentPage(1)
    setActiveCategory(categoryId)
    setSearchQuery('') // Clear search when category is selected
    setAuthor('') // Clear author when category is selected
    navigate(categoryId ? `/news?c=${categorySlug}` : '/news/', { preventScrollReset: true })
  }

  const resetAuthor = () => {
    if (author) {
      setCurrentPage(1) // Reset to page 1
      setAuthor('')
      navigate('/news/', { replace: true, preventScrollReset: true })
    }
  }

  const handleSearch = (e) => {
    setCurrentPage(1)
    const searchValue = e.target.value
    setSearchQuery(searchValue)
    setAuthor('') // Clear author when searching
    setActiveCategory('') // Clear category when searching

    // Update the URL with the search query in WordPress style
    navigate(searchValue ? `/news?s=${encodeURIComponent(searchValue)}` : '/news', {
      replace: true, // Avoid adding multiple entries in the browser history
      preventScrollReset: true,
    })
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1)
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1)
  }

  const handlePageClick = (page) => {
    setCurrentPage(page)
  }

  const getFullUrl = (path) => `${baseUrl}/${path}`

  return (
    <>
      <Header title="WHAT'S NEW AT TALENTLIFT" backgroundimage="/images/news/news_background.png" ishomepage={false} />
      <main>
        <section className="tl-container">
          <div className="flex flex-col lg:flex-row justify-between items-center mb-8">
            <div className="flex mb-4 md:mb-0 flex-wrap">
              {wpCategories.map((category) => (
                <button
                  key={category.id}
                  className={`${
                    activeCategory === category.id ? 'text-pointed' : 'text-gray-500'
                  } text-sm md:text-lg font-semibold mr-4 md:mr-8`}
                  onClick={() => handleCategoryClick(category.id, category.slug)}
                >
                  {category.name}
                </button>
              ))}

              {/* Author Filter */}
              {author && (
                <div className="flex items-center text-pointed text-sm md:text-lg font-semibold mr-4">
                  {`Author: ${authorName}`}
                  <button
                    className="ml-2 text-gray-500 hover:text-pointed"
                    onClick={() => resetAuthor()} // Removes the author filter
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
            <div className="relative w-full lg:w-auto lg:ml-10">
              <input
                type="text"
                placeholder="Search"
                className="border border-gray-300 rounded-full py-2 px-4 pl-10 w-full xl:w-64"
                value={searchQuery}
                onChange={handleSearch}
              />
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute top-2 left-3" />
            </div>
          </div>
          <div className="space-y-4">
            {loading ? (
              <Loading />
            ) : posts.length === 0 ? (
              <div className="group flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-lg shadow-sm bg-grey5 hover:bg-grey10">
                No records found
              </div>
            ) : (
              posts.map((post) => {
                const postDate = new Date(post.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                })

                const isNavigatingToPost =
                  navigation.state === 'loading' && navigation.location.pathname === `/${post.slug}`

                return (
                  <div
                    key={post.id}
                    className="group flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-lg shadow-sm bg-grey5 hover:bg-grey10"
                  >
                    {isNavigatingToPost ? (
                      <Loading />
                    ) : (
                      <Link
                        to={`/${post.slug}`}
                        state={{ from: location.pathname }}
                        className="flex flex-col lg:flex-row lg:items-center w-full"
                      >
                        <div className="mr-0 mb-2 md:mb-0 lg:self-center lg:p-2 flex-shrink-0 lg:mr-4">
                          <span className="text-sm bg-pointed text-white rounded-full py-1 px-3 inline-flex max-w-full">
                            {getCategoryName(post.categories[0], wpCategories)}
                          </span>
                          <span className="block text-sm text-gray-500">{postDate}</span>
                        </div>
                        {isClient ? (
                          <div dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                        ) : (
                          <div>{decodeHTMLEntities(post.title.rendered)}</div>
                        )}
                      </Link>
                    )}
                    <div className="flex items-center space-x-4 mt-2 md:mt-0">
                      <SharePopover url={getFullUrl(post.slug)} />
                      <Link to={`/${post.slug}`}>
                        <ArrowUpIcon className="w-6 h-6 text-pointed cursor-pointer rotate-45 group-hover:rotate-90" />
                      </Link>
                    </div>
                  </div>
                )
              })
            )}
          </div>
          <div className="text-sm text-gray-600 mt-4">
            Showing page {currentPage} of {totalPages}, total posts: {totalPosts}.
          </div>

          <div className="flex justify-between items-center mt-8">
            <button
              className="px-4 py-2 bg-grey5 hover:bg-grey10 rounded disabled:opacity-50"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <div>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  className={`px-3 py-1 mx-1 rounded ${currentPage === index + 1 ? 'bg-pointed text-white' : 'bg-grey5'} hover:bg-pointed`}
                  onClick={() => handlePageClick(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <button
              className="px-4 py-2 bg-grey5 hover:bg-grey10 rounded disabled:opacity-50"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
